import {createTheme} from '@mui/material/styles';
import {t} from './statamicTokens';
import {BRAND} from './brand';

/**
 * Minimal MUI theme driven by Statamic tokens. Only covers the MUI surfaces
 * that React Admin or stray MUI components render unstyled (buttons, inputs,
 * tables, etc.). Our Statamic primitives already apply their own sx, so this
 * theme is a safety net for anything not covered by a primitive.
 */
const statamicMuiTheme = createTheme({
    palette: {
        mode: 'light',
        primary: {
            main: BRAND.main,
            light: BRAND.tint,
            dark: BRAND.active,
            contrastText: BRAND.on,
        },
        secondary: {
            main: t.color.status.infoSolid,
            light: t.color.status.infoTint,
            contrastText: '#FFFFFF',
        },
        success: {
            main: t.color.status.successSolid,
            light: t.color.status.successTint,
            contrastText: '#FFFFFF',
        },
        warning: {
            main: t.color.status.warningSolid,
            light: t.color.status.warningTint,
            contrastText: '#FFFFFF',
        },
        error: {
            main: t.color.status.dangerSolid,
            light: t.color.status.dangerTint,
            contrastText: '#FFFFFF',
        },
        info: {
            main: t.color.status.infoSolid,
            light: t.color.status.infoTint,
            contrastText: '#FFFFFF',
        },
        background: {
            default: t.color.page,
            paper: t.color.surface,
        },
        text: {
            primary: t.color.text.primary,
            secondary: t.color.text.secondary,
            disabled: t.color.text.muted,
        },
        divider: t.color.border.default,
        action: {
            hover: t.color.sidebarHover,
            selected: t.color.accent.primaryTint,
        },
    },

    typography: {
        fontFamily: t.font.sans,
        h1: {fontSize: 28, fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.01em'},
        h2: {fontSize: t.type.display.size, fontWeight: t.type.display.weight, lineHeight: t.type.display.lineHeight},
        h3: {fontSize: t.type.h2.size,      fontWeight: t.type.h2.weight,      lineHeight: t.type.h2.lineHeight},
        h4: {fontSize: t.type.h3.size,      fontWeight: t.type.h3.weight,      lineHeight: t.type.h3.lineHeight},
        body1:    {fontSize: t.type.body.size,    lineHeight: t.type.body.lineHeight},
        body2:    {fontSize: t.type.small.size,   lineHeight: t.type.small.lineHeight},
        caption:  {fontSize: t.type.caption.size, lineHeight: t.type.caption.lineHeight},
        button:   {textTransform: 'none', fontWeight: 500},
    },

    shape: {
        borderRadius: t.radius.sm,
    },

    shadows: [
        'none',
        t.shadow.panel,
        t.shadow.panel,
        t.shadow.dropdown,
        t.shadow.dropdown,
        t.shadow.dropdown,
        t.shadow.modal,
        t.shadow.modal,
        t.shadow.modal,
        t.shadow.modal,
        t.shadow.modal,
        t.shadow.modal,
        t.shadow.modal,
        t.shadow.modal,
        t.shadow.modal,
        t.shadow.modal,
        t.shadow.modal,
        t.shadow.modal,
        t.shadow.modal,
        t.shadow.modal,
        t.shadow.modal,
        t.shadow.modal,
        t.shadow.modal,
        t.shadow.modal,
        t.shadow.modal,
    ],

    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    fontFamily: t.font.sans,
                    backgroundColor: t.color.page,
                    color: t.color.text.primary,
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale',
                },
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
                disableRipple: true,
            },
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    borderRadius: t.radius.sm,
                    boxShadow: 'none',
                    fontWeight: 500,
                    '&:hover': {boxShadow: 'none'},
                },
            },
        },
        MuiPaper: {
            defaultProps: {elevation: 0},
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                },
            },
        },
        MuiCard: {
            defaultProps: {elevation: 0},
            styleOverrides: {
                root: {
                    border: `1px solid ${t.color.border.default}`,
                    borderRadius: t.radius.md,
                    boxShadow: 'none',
                },
            },
        },
        MuiTextField: {
            defaultProps: {variant: 'outlined', size: 'small'},
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: t.radius.sm,
                    backgroundColor: t.color.surface,
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: t.color.border.default,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: t.color.border.strong,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: t.color.accent.primary,
                        borderWidth: 1,
                    },
                    '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${t.color.accent.primaryTint}`,
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: t.radius.sm,
                    fontWeight: 500,
                },
            },
        },
        MuiTableHead: {
            styleOverrides: {
                root: {
                    backgroundColor: t.color.surface,
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderBottom: `1px solid ${t.color.border.hairline}`,
                    fontSize: t.type.body.size,
                },
                head: {
                    fontWeight: 600,
                    color: t.color.text.secondary,
                    fontSize: t.type.caption.size,
                    backgroundColor: t.color.surface,
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: t.radius.lg,
                    border: `1px solid ${t.color.border.default}`,
                    boxShadow: t.shadow.modal,
                },
            },
        },
        MuiAppBar: {
            defaultProps: {color: 'default', elevation: 0},
            styleOverrides: {
                root: {
                    backgroundColor: t.color.surface,
                    borderBottom: `1px solid ${t.color.border.default}`,
                    color: t.color.text.primary,
                    boxShadow: 'none',
                },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: t.color.border.hairline,
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    fontSize: t.type.caption.size,
                    backgroundColor: t.color.text.primary,
                    color: t.color.text.inverse,
                    borderRadius: t.radius.sm,
                },
            },
        },
        MuiLink: {
            defaultProps: {underline: 'hover'},
            styleOverrides: {
                root: {
                    color: t.color.accent.primary,
                },
            },
        },
    },
});

export default statamicMuiTheme;
